import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileText, ShieldCheck, Zap, BookOpen } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/35530601/pexels-photo-35530601.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const Landing = () => {
  const { t, lang } = useLanguage();
  const { user } = useAuth();

  const features = [
    {
      icon: FileText,
      title: lang === "ar" ? "إخراج Word منسّق" : "Formatted Word output",
      desc:
        lang === "ar"
          ? "ملف .docx جاهز، مع رأس وتذييل، وفهرس وقائمة مراجع."
          : "Ready .docx with headers, TOC, and reference list.",
    },
    {
      icon: ShieldCheck,
      title: lang === "ar" ? "مراجع موثّقة" : "Verified citations",
      desc:
        lang === "ar"
          ? "نتحقق من المراجع عبر Crossref ولا نختلق مصادر."
          : "References checked via Crossref — no hallucinated sources.",
    },
    {
      icon: Zap,
      title: lang === "ar" ? "تسليم آلي" : "Async delivery",
      desc:
        lang === "ar"
          ? "اطلب وأغلق المتصفح؛ نُرسل لك البريد عند الاكتمال."
          : "Order, close the tab, we email you when it's done.",
    },
    {
      icon: BookOpen,
      title: lang === "ar" ? "أدوات تعليمية" : "Educational use",
      desc:
        lang === "ar"
          ? "تُقدَّم المخرجات كأدوات استرشادية تعليمية فقط."
          : "Output is provided as an educational aid only.",
    },
  ];

  return (
    <main className="min-h-[calc(100vh-4rem)]" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 grain-overlay opacity-60 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-16 lg:py-24 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-5 sm:space-y-7">
            {/* High-contrast status badge with a live pulse dot for readability */}
            <span
              className="inline-flex items-center gap-2 text-[13px] font-semibold px-3.5 py-1.5 rounded-full bg-slate-900 text-white shadow-sm ring-1 ring-slate-900/10"
              data-testid="hero-tagline-chip"
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              {t("tagline")}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.15] sm:leading-[1.1] text-balance">
              {t("hero_title")}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-8 sm:leading-9 max-w-xl">
              {t("hero_sub")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Primary CTA — "ابدأ الآن": routes to dashboard when signed in. */}
              <Link to={user ? "/dashboard" : "/register"} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="bg-slate-900 hover:bg-slate-800 px-7 w-full sm:w-auto h-12 text-base shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
                  data-testid="hero-cta-register-btn"
                >
                  {t("cta_register")}
                </Button>
              </Link>
              {/* Secondary CTA — flips to "لوحة التحكم" for logged-in users. */}
              <Link to={user ? "/dashboard" : "/login"} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-7 w-full sm:w-auto h-12 text-base border-slate-300 transition duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-slate-400 hover:bg-slate-50 active:translate-y-0"
                  data-testid="hero-cta-login-btn"
                >
                  {user ? (lang === "ar" ? "لوحة التحكم" : "Dashboard") : t("cta_login")}
                </Button>
              </Link>
            </div>
          </div>
          {/* Hero image — responsive, rounded-2xl, soft glow + hover zoom.
              Stacks below the copy on mobile via the parent grid. */}
          <div className="relative group">
            <div className="absolute -inset-3 hidden lg:block bg-gradient-to-tr from-slate-200/70 via-slate-100/40 to-transparent rounded-3xl blur-2xl pointer-events-none" />
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-slate-200 shadow-md ring-1 ring-slate-900/5">
              <img
                src={HERO_IMG}
                alt={lang === "ar" ? "مكتبة أكاديمية" : "Academic library"}
                loading="eager"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-8 sm:mb-10 text-balance">
          {lang === "ar" ? "لماذا زبطنا؟" : "Why Zabatna?"}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {features.map((f) => (
            <Card
              key={f.title}
              className="p-5 sm:p-6 border border-slate-200 shadow-none hover:shadow-sm transition-all duration-200"
              data-testid={`feature-card-${f.title}`}
            >
              <f.icon className="w-6 h-6 text-slate-900 mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* DISCLOSURE STRIP */}
      <section className="border-t border-slate-200 bg-slate-50/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
          <p
            className="text-sm text-slate-600 leading-relaxed text-balance"
            data-testid="landing-disclaimer-line"
          >
            {t("terms_disclaimer")}
          </p>
        </div>
      </section>
    </main>
  );
};

export default Landing;
